import { Component, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import {
  DocumentInfo,
  IconType,
  StoreGroup,
  StoreTemplateStatus,
  StoreWidget,
} from '@pt/pt-models';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { NgrxWidgetSelectors } from '../store/';
import { SetChapterCompletedRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SetChapterCompletedRequest';
import {
  BusinessProjectManagementService,
  DownloadService,
  UsersService,
} from '@pt/api-client';
import { CommonDocumentEditorDetails } from '@primatoo-ms-organization';
import { AvailableOperations } from 'it/primatoo/openapi/api/org/schemas/common/AvailableOperations';
import { User } from 'it/primatoo/openapi/api/org/schemas/user/User';
import { ToastrService } from 'ngx-toastr';
import { map } from 'rxjs/operators';
import { DocAccess } from 'it/primatoo/openapi/api/org/schemas/common/DocAccess'; // adatta il path se IconType vive altrove

type FeedbackKind = 'success' | 'error';

interface Feedback {
  kind: FeedbackKind;
  messageKey: string;
  detail?: string;
}

@Component({
  selector: 'pt-complete-status',
  standalone: false,
  templateUrl: './complete-status-dialog.component.html',
  styles: [
    `
      .tooltip-icon {
        cursor: pointer;
        font-size: 0.85rem;
        line-height: 1;
        user-select: none;
      }

      ::ng-deep .tooltip-lg {
        .tooltip-inner {
          max-width: 280px;
          padding: 0.6rem 0.75rem;
          font-size: 0.8rem;
          text-align: left;
        }
      }
      pre {
        font-family: var(--bs-font-monospace);
      }

      .tab-content-scroll {
        height: 570px;
        overflow: auto;
      }

      /* Header documento: tre colonne (icona | testo | id) allineate al centro */
      .doc-header__icon {
        width: 56px;
        height: 56px;
        background: #fff;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 0.5rem;
      }
      .doc-header__title {
        font-size: 1.05rem;
        font-weight: 500;
        line-height: 1.25;
        margin-bottom: 0.25rem;
      }
      .doc-header__meta {
        line-height: 1;
      }
      .doc-header__id code {
        font-size: 0.85em;
      }

      /* Card statistica compatta: numero piu' piccolo, evita lo "stiramento" verticale */
      .stat-card .h5 {
        font-size: 1.1rem;
      }
      .stat-card .small {
        font-size: 0.72rem;
      }
      .copy-btn {
        position: absolute;
        top: 0.75rem;
        right: 0.75rem;
        z-index: 1;
        display: flex;
        align-items: center;
        gap: 0.25rem;
      }
      .chapter-info-bar {
        background-color: var(--bs-light);
        border-color: var(--bs-border-color) !important;
      }
    `,
  ],
})
export class CompleteStatusDialogComponent {
  readonly DocAccess = DocAccess;

  private readonly userService = inject(UsersService);
  protected creatorUser: WritableSignal<User | null> = signal(null);
  readonly IconType = IconType;
  private readonly toastr = inject(ToastrService);
  activeTab:
    | 'summary'
    | 'status'
    | 'chapter'
    | 'docInfo'
    | 'permissions'
    | 'editorDetails' = 'summary';

  saving = false;
  feedback: Feedback | null = null;

  completedCtrl = new FormControl<number>(0, {
    nonNullable: true,
    validators: [Validators.required, Validators.min(0), Validators.max(100)],
  });

  status$: Observable<StoreTemplateStatus> = this.store.select(
    NgrxWidgetSelectors.getWidgetsStatus
  );

  getActiveChapterInfo$: Observable<Chapter> = this.store.select(
    NgrxWidgetSelectors.getActiveChapterInfo
  );

  documentPermission$: Observable<AvailableOperations> = this.store.select(
    NgrxWidgetSelectors.getDocumentPermission
  );
  widgets$: Observable<StoreWidget[]> = this.store.select(
    NgrxWidgetSelectors.getStoreWidgets
  );

  groups$: Observable<StoreGroup[]> = this.store.select(
    NgrxWidgetSelectors.getChapterGroups
  );

  storeDetails$ = combineLatest([this.widgets$, this.groups$]).pipe(
    map(([widgets, groups]) => ({ widgets, groups }))
  );

  /**
   * Input opzionale: passato dal chiamante via `modalRef.componentInstance.commonDocumentEditorDetails = ...`
   * Tipizzato `any` per evitare l'import circolare; tipizza al tipo reale se disponibile.
   */
  commonDocumentEditorDetails?: CommonDocumentEditorDetails;

  documentInfo$ = this.store.select(NgrxWidgetSelectors.getDocInfo);

  private currentChapter?: Chapter;
  private documentInfo?: DocumentInfo;

  getValidWidgets(widgets: StoreWidget[]): number {
    return widgets?.filter((w) => w.isValid)?.length ?? 0;
  }

  getEnabledGroups(groups: StoreGroup[]): number {
    return groups?.filter((g) => g.enabled)?.length ?? 0;
  }

  copyToClipboard(data: unknown): void {
    const text = JSON.stringify(data, null, 2);

    // Metodo moderno (HTTPS / browser supportati)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.showCopyFeedback();
        })
        .catch(() => {
          this.fallbackCopyToClipboard(text);
        });
    } else {
      // Fallback per HTTP o browser meno recenti
      this.fallbackCopyToClipboard(text);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();

    try {
      document.execCommand('copy');
      this.showCopyFeedback();
    } catch (e) {
      console.warn('Copy fallback failed', e);
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private showCopyFeedback(): void {
    this.toastr.success('Dati copiati negli appunti', 'Copia', {
      timeOut: 2000,
      positionClass: 'toast-center-center',
    });
  }

  constructor(
    public activeModal: NgbActiveModal,
    private downloadService: DownloadService,
    private store: Store
  ) {
    this.getActiveChapterInfo$
      .subscribe((c) => {
        this.currentChapter = c;
        if (c?.completed != null) {
          this.completedCtrl.setValue(c.completed, { emitEvent: false });
        }
      })
      .unsubscribe();

    this.documentInfo$
      .subscribe((d) => {
        this.documentInfo = d;
        this.loadCreateUser(d.creatorUserId);
      })
      .unsubscribe();
  }

  dismiss(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    this.feedback = null;

    const chapterSelector = this.currentChapter?.selector;
    const chapterId = this.currentChapter?.id;
    const docType = this.documentInfo?.docType;
    const docId = this.documentInfo?.docId;

    if (
      this.completedCtrl.invalid ||
      !chapterSelector ||
      chapterId == null ||
      !docType ||
      !docId
    ) {
      this.feedback = {
        kind: 'error',
        messageKey: 'PAGE.COMPLETE_STATUS.FEEDBACK.MISSING_CONTEXT',
      };
      return;
    }

    const request: SetChapterCompletedRequest = {
      chapter: chapterSelector,
      completed: this.completedCtrl.value,
      ghostOperation: true,
    };

    const service: BusinessProjectManagementService =
      this.downloadService.getDocumentEditorTypeService(docType);

    this.saving = true;
    service.completed(docId, chapterId, request).subscribe({
      next: () => {
        this.saving = false;
        this.feedback = {
          kind: 'success',
          messageKey: 'PAGE.COMPLETE_STATUS.FEEDBACK.SAVE_SUCCESS',
        };
      },
      error: (err) => {
        this.saving = false;
        this.feedback = {
          kind: 'error',
          messageKey: 'PAGE.COMPLETE_STATUS.FEEDBACK.SAVE_ERROR',
          detail: this.extractErrorDetail(err),
        };
      },
    });
  }

  canSave(
    chapter: Chapter | null | undefined,
    doc: DocumentInfo | null | undefined,
    ctrl: FormControl<number>
  ): boolean {
    if (this.activeTab !== 'summary') {
      return false;
    }
    return this.canSaveContext(chapter, doc) && ctrl.valid;
  }

  canSaveContext(
    chapter: Chapter | null | undefined,
    doc: DocumentInfo | null | undefined
  ): boolean {
    if (!chapter || doc?.status !== 'DRAFT') {
      return false;
    }
    // Se il capitolo non dichiara stati supportati, il vincolo sullo stato del capitolo non si applica.
    const hasStates =
      !!chapter.supportedStatus && chapter.supportedStatus.length > 0;
    return !hasStates || chapter.currentStatus === 'WORKING';
  }

  validityRate(status: StoreTemplateStatus | null | undefined): number {
    const total = status?.totalWidget ?? 0;
    const valid = status?.validWidgetId?.length ?? 0;
    if (!total) return 0;
    return Math.round((valid / total) * 100);
  }

  private extractErrorDetail(err: unknown): string | undefined {
    if (!err || typeof err !== 'object') return undefined;
    const e = err as {
      status?: number;
      error?: { message?: string };
      message?: string;
    };
    if (e.error?.message) return e.error.message;
    if (e.status) return `HTTP ${e.status}`;
    return e.message;
  }

  private loadCreateUser(creatorUserId: number) {
    this.userService.findById(creatorUserId).subscribe((u) => {
      this.creatorUser.set(u);
    });
  }
}
